// file: client side ./algorithm/tableHelpers.js

// --- Time and Shift Helpers ---

/**
 * Parses a time string ("HH:MM") into minutes since midnight.
 * Returns NaN if the format is invalid.
 */
function parseTime(timeStr) {
    if (!timeStr || typeof timeStr !== 'string') return NaN;
    const parts = timeStr.split(':');
    if (parts.length !== 2) return NaN;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return NaN;
    }
    return hours * 60 + minutes;
}

const shifts = {
    breakfast: { start: '07:00', end: '11:00' },
    lunch: { start: '11:00', end: '16:00' },
    dinner: { start: '16:00', end: '23:00' }, // Adjust end time if needed
};

/**
 * Determines the meal type ('breakfast', 'lunch', 'dinner') for a given time string ("HH:MM").
 * Returns null if the time doesn't fall into a defined shift.
 */
function getMealTypeByTime(timeStr) {
    const time = parseTime(timeStr);
    if (isNaN(time)) return null;

    for (const [mealType, shift] of Object.entries(shifts)) {
        const start = parseTime(shift.start);
        const end = parseTime(shift.end);
        // Handle potential errors from parseTime if shift definitions are invalid
        if (isNaN(start) || isNaN(end)) continue;

        // Check if time falls within the shift range [start, end)
        if (time >= start && time < end) {
            return mealType;
        }
    }
    return null; // Return null if no matching shift is found
}


// --- Table Fetching ---

/**
 * Extracts and processes table data from the restaurantData object.
 * Includes temporary table properties and sorts tables.
 * @param {Object} restaurantData - The main restaurant data object.
 * @returns {Array} An array of processed table objects.
 */
function getAllTables(restaurantData) {
    let allTables = [];
    if (restaurantData?.floors && Array.isArray(restaurantData.floors)) {
        restaurantData.floors.forEach(floor => {
            if (floor?.tables && Array.isArray(floor.tables)) {
                floor.tables.forEach(tbl => {
                    // Ensure table number, capacities, priority exist before parsing
                    const tableNumberRaw = tbl.tableNumber?.$numberInt ?? tbl.tableNumber;
                    const minCapacityRaw = tbl.minCapacity?.$numberInt ?? tbl.minCapacity;
                    const maxCapacityRaw = tbl.maxCapacity?.$numberInt ?? tbl.maxCapacity;
                    const priorityRaw = tbl.priority?.$numberInt ?? tbl.priority;
                    const xRaw = tbl.x?.$numberInt ?? tbl.x;
                    const yRaw = tbl.y?.$numberInt ?? tbl.y;

                    if (tbl.objectType === "Tafel" &&
                        tableNumberRaw !== undefined &&
                        minCapacityRaw !== undefined &&
                        maxCapacityRaw !== undefined &&
                        priorityRaw !== undefined &&
                        xRaw !== undefined &&
                        yRaw !== undefined)
                    {
                        allTables.push({
                            tableId: tbl.id,
                            tableNumber: parseInt(tableNumberRaw, 10),
                            minCapacity: parseInt(minCapacityRaw, 10),
                            maxCapacity: parseInt(maxCapacityRaw, 10),
                            priority: parseInt(priorityRaw, 10),
                            x: parseInt(xRaw, 10),
                            y: parseInt(yRaw, 10),
                            isTemporary: tbl.isTemporary === true, // Ensure boolean
                            startDate: tbl.startDate || null, // Expects 'YYYY-MM-DD'
                            endDate: tbl.endDate || null,     // Expects 'YYYY-MM-DD'
                            application: tbl.application || null // Expects 'breakfast', 'lunch', or 'dinner'
                        });
                    } else if (tbl.objectType === "Tafel") {
                        console.warn(`Skipping table due to missing essential properties: ${JSON.stringify(tbl)}`);
                    }
                });
            }
        });
    } else {
        console.warn("Restaurant data is missing 'floors' array.");
    }

    // Sort tables
    allTables.sort((a, b) => {
        if (a.maxCapacity !== b.maxCapacity) {
            return a.maxCapacity - b.maxCapacity;
        }
        if (a.priority !== b.priority) {
            // Assuming lower priority number means higher priority
            return a.priority - b.priority;
        }
        return a.minCapacity - b.minCapacity;
    });

    // Filter out tables where parsing failed (resulted in NaN)
     allTables = allTables.filter(t =>
        !isNaN(t.tableNumber) &&
        !isNaN(t.minCapacity) &&
        !isNaN(t.maxCapacity) &&
        !isNaN(t.priority) &&
        !isNaN(t.x) &&
        !isNaN(t.y)
    );

    return allTables;
}


// --- Temporary Table Validation ---

/**
 * Checks if a temporary table is valid for a specific reservation date and time.
 * @param {Object} table - The table object (must include isTemporary, startDate, endDate, application).
 * @param {string} reservationDateStr - The date of the reservation ("YYYY-MM-DD").
 * @param {string} reservationTimeStr - The time of the reservation ("HH:MM").
 * @returns {boolean} True if the table is valid, false otherwise.
 */
function isTemporaryTableValid(table, reservationDateStr, reservationTimeStr) {
    if (!table.isTemporary) {
        return true; // Not temporary, always valid (subject to other checks like availability)
    }

    // Check date range
    if (!table.startDate || !table.endDate) {
        console.log(`Temporary Table ${table.tableNumber} skipped: Missing start/end date.`);
        return false; // Invalid temporary table definition
    }
    // Basic date string comparison (YYYY-MM-DD format)
    if (reservationDateStr < table.startDate || reservationDateStr > table.endDate) {
        // console.log(`Temporary Table ${table.tableNumber} skipped: Date ${reservationDateStr} outside range ${table.startDate}-${table.endDate}.`); // Optional verbose logging
        return false;
    }

    // Check application (meal type/shift)
    const reservationMealType = getMealTypeByTime(reservationTimeStr);
    if (!reservationMealType) {
         console.log(`Temporary Table ${table.tableNumber} skipped: Could not determine meal type for time ${reservationTimeStr}.`);
        return false; // Cannot determine meal type for the reservation
    }
    if (table.application !== reservationMealType) {
        // console.log(`Temporary Table ${table.tableNumber} skipped: Application '${table.application}' does not match reservation meal type '${reservationMealType}'.`); // Optional verbose logging
        return false;
    }

    // console.log(`Temporary Table ${table.tableNumber} is valid for ${reservationDateStr} at ${reservationTimeStr} (${reservationMealType}).`); // Optional verbose logging
    return true;
}


// --- Exports ---
// Use CommonJS exports (adjust if using ES6 modules)
module.exports = {
    shifts,
    parseTime, // Export if needed elsewhere, otherwise could be kept internal
    getMealTypeByTime,
    getAllTables,
    isTemporaryTableValid
};